import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { 
  ArrowUp, 
  ArrowDown,
  Target,
  Users,
  TrendingUp
} from 'lucide-react';
import RequirementSubmissionsTable from './RequirementSubmissionsTable';
import RecruiterPerformanceTable from './RecruiterPerformanceTable';

interface RequirementSubmission {
  requirement: string;
  submissions: number;
  avgScore: number;
  client: string;
  status: 'active' | 'inactive' | 'closed';
  createdDate: string;
  lastSubmission: string;
}

interface RecruiterPerformance {
  recruiter: string;
  manager: string;
  totalSubmissions: number;
  fitmentScores: {
    '90-100': number;
    '80-90': number;
    '70-80': number;
    '60-70': number;
    '50-60': number;
    '40-50': number;
    '<40': number;
  };
}

interface ClientBreakdown {
  client: string;
  requirements: number;
  positions: number;
  submissions: number;
  avgFitmentScore?: number;
}

interface FitmentScoreDistribution {
  range: string;
  count: number;
  percentage: number;
}

interface DayWiseSubmissions {
  date: string;
  submissions: number;
}

interface ManagerWiseSubmissions {
  manager: string;
  submissions: number;
}

interface EnhancedAnalyticsTablesProps {
  requirementWiseSubmissions: RequirementSubmission[];
  recruiterPerformance: RecruiterPerformance[];
  clientBreakdown: ClientBreakdown[];
  fitmentScoreDistribution: FitmentScoreDistribution[];
  dayWiseSubmissions: DayWiseSubmissions[];
  managerWiseSubmissions: ManagerWiseSubmissions[];
  isLoading?: boolean;
}

export const EnhancedAnalyticsTables: React.FC<EnhancedAnalyticsTablesProps> = ({
  requirementWiseSubmissions,
  recruiterPerformance,
  clientBreakdown,
  fitmentScoreDistribution,
  dayWiseSubmissions,
  managerWiseSubmissions,
  isLoading = false
}) => {
  const [activeTable, setActiveTable] = useState<'requirements' | 'recruiters' | 'clients' | 'scores' | 'daily' | 'managers'>('requirements');

  const tables = [
    { id: 'requirements' as const, label: 'Requirements', count: requirementWiseSubmissions.length },
    { id: 'recruiters' as const, label: 'Recruiters', count: recruiterPerformance.length },
    { id: 'clients' as const, label: 'Clients', count: clientBreakdown.length },
    { id: 'scores' as const, label: 'Score Distribution', count: fitmentScoreDistribution.length },
    { id: 'daily' as const, label: 'Daily Trends', count: dayWiseSubmissions.length },
    { id: 'managers' as const, label: 'Managers', count: managerWiseSubmissions.length }
  ];

  const renderClientTable = () => (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-xl">
          <Users className="w-6 h-6 text-primary-500" />
          <span>Client Performance Table</span>
        </CardTitle>
        <CardDescription className="text-base">
          Detailed breakdown of client performance metrics
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-4 px-4 text-lg font-semibold">Client</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Requirements</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Positions</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Uploads</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Avg Fitment Score</th>
              </tr>
            </thead>
            <tbody>
              {clientBreakdown.map((client, index) => (
                <tr key={index} className="border-b border-glass-border dark:border-glass-border-dark-mode hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 transition-colors">
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-lg font-semibold text-text-primary">{client.client}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-primary-500">{client.requirements}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-secondary-500">{client.positions || 0}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-accent-500">{client.submissions}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-success-500">
                      {client.avgFitmentScore !== undefined && client.avgFitmentScore !== null
                        ? `${client.avgFitmentScore.toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderScoreDistributionTable = () => (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-xl">
          <Target className="w-6 h-6 text-accent-500" />
          <span>Fitment Score Distribution Table</span>
        </CardTitle>
        <CardDescription className="text-base">
          Detailed breakdown of fitment score ranges and percentages
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-4 px-4 text-lg font-semibold">Score Range</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Count</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Percentage</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Visual</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {fitmentScoreDistribution.map((score, index) => (
                <tr key={index} className="border-b border-glass-border dark:border-glass-border-dark-mode hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 transition-colors">
                  <td className="py-4 px-4">
                    <span className="text-lg font-semibold text-text-primary">{score.range}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-primary-500">{score.count}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-accent-500">{score.percentage}%</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            score.range.includes('Excellent') ? 'bg-green-500' :
                            score.range.includes('Good') ? 'bg-blue-500' :
                            score.range.includes('Average') && !score.range.includes('Below') ? 'bg-amber-500' :
                            score.range.includes('Below Average') ? 'bg-orange-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(score.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-base font-medium ${
                      score.range.includes('Excellent') ? 'bg-success-500/20 text-success-500' :
                      score.range.includes('Good') ? 'bg-primary-500/20 text-primary-500' :
                      score.range.includes('Average') && !score.range.includes('Below') ? 'bg-warning-500/20 text-warning-500' :
                      score.range.includes('Below Average') ? 'bg-orange-500/20 text-orange-500' :
                      'bg-error-500/20 text-error-500'
                    }`}>
                      {score.range.includes('Excellent') ? 'Excellent' :
                       score.range.includes('Good') ? 'Good' :
                       score.range.includes('Average') && !score.range.includes('Below') ? 'Average' :
                       score.range.includes('Below Average') ? 'Below Avg' : 'Poor'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderDailyTrendsTable = () => (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-xl">
          <TrendingUp className="w-6 h-6 text-secondary-500" />
          <span>Daily Uploads Trend</span>
        </CardTitle>
        <CardDescription className="text-base">
          Day-wise upload trends and performance metrics
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-4 px-4 text-lg font-semibold">Date</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Uploads</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Trend</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Performance</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {[...dayWiseSubmissions].reverse().map((day, index) => {
                const sortedDays = [...dayWiseSubmissions].reverse();
                const prevDay = sortedDays[index - 1];
                const trend = prevDay ? day.submissions - prevDay.submissions : 0;
                return (
                  <tr key={day.date} className="border-b border-glass-border dark:border-glass-border-dark-mode hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 transition-colors">
                    <td className="py-4 px-4">
                      <span className="text-lg font-semibold text-text-primary">{day.date}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-lg font-bold text-secondary-500">{day.submissions}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        {trend > 0 ? (
                          <ArrowUp className="w-4 h-4 text-success-500" />
                        ) : trend < 0 ? (
                          <ArrowDown className="w-4 h-4 text-error-500" />
                        ) : (
                          <div className="w-4 h-4 bg-text-secondary rounded-full" />
                        )}
                        <span className={`text-base font-semibold ${
                          trend > 0 ? 'text-success-500' :
                          trend < 0 ? 'text-error-500' :
                          'text-text-secondary'
                        }`}>
                          {trend > 0 ? `+${trend}` : trend < 0 ? trend : '0'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-secondary-500 to-accent-500 h-2 rounded-full"
                            style={{ width: `${Math.min((day.submissions / 20) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-base text-text-secondary">
                          {Math.min((day.submissions / 20) * 100, 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-base font-medium ${
                        day.submissions >= 15 ? 'bg-success-500/20 text-success-500' :
                        day.submissions >= 10 ? 'bg-primary-500/20 text-primary-500' :
                        day.submissions >= 5 ? 'bg-warning-500/20 text-warning-500' :
                        'bg-error-500/20 text-error-500'
                      }`}>
                        {day.submissions >= 15 ? 'High' :
                         day.submissions >= 10 ? 'Good' :
                         day.submissions >= 5 ? 'Average' : 'Low'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderManagerTable = () => (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-xl">
          <Users className="w-6 h-6 text-accent-500" />
          <span>Manager Performance Table</span>
        </CardTitle>
        <CardDescription className="text-base">
          Manager-wise performance metrics and submission counts
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-4 px-4 text-lg font-semibold">Manager</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Uploads</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Performance</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Rank</th>
                <th className="text-left py-4 px-4 text-lg font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {managerWiseSubmissions
                .sort((a, b) => b.submissions - a.submissions)
                .map((manager, index) => (
                <tr key={index} className="border-b border-glass-border dark:border-glass-border-dark-mode hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 transition-colors">
                  <td className="py-4 px-4">
                    <span className="text-lg font-semibold text-text-primary">{manager.manager}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-lg font-bold text-accent-500">{manager.submissions}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-accent-500 to-primary-500 h-2 rounded-full"
                          style={{ width: `${Math.min((manager.submissions / 100) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-base text-text-secondary">
                        {Math.min((manager.submissions / 100) * 100, 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`text-lg font-bold ${
                      index === 0 ? 'text-success-500' :
                      index === 1 ? 'text-primary-500' :
                      index === 2 ? 'text-warning-500' :
                      'text-text-secondary'
                    }`}>
                      #{index + 1}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-base font-medium ${
                      index === 0 ? 'bg-success-500/20 text-success-500' :
                      index < 3 ? 'bg-primary-500/20 text-primary-500' :
                      'bg-warning-500/20 text-warning-500'
                    }`}>
                      {index === 0 ? 'Top Performer' :
                       index < 3 ? 'High Performer' :
                       'Good Performer'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Table Navigation */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-3">
            {tables.map((table) => (
              <Button
                key={table.id}
                variant={activeTable === table.id ? "default" : "outline"}
                size="lg"
                onClick={() => setActiveTable(table.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-lg font-semibold ${
                  activeTable === table.id 
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25' 
                    : 'text-text-secondary hover:text-text-primary hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50'
                }`}
              >
                <span>{table.label}</span>
                <span className="bg-glass-surface dark:bg-glass-surface-dark-mode text-text-secondary px-2 py-1 rounded-full text-sm">
                  {table.count}
                </span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Table Content */}
      {activeTable === 'requirements' && (
        <RequirementSubmissionsTable
          data={requirementWiseSubmissions}
          isLoading={isLoading}
        />
      )}

      {activeTable === 'recruiters' && (
        <RecruiterPerformanceTable
          data={recruiterPerformance}
          isLoading={isLoading}
        />
      )}

      {activeTable === 'clients' && renderClientTable()}

      {activeTable === 'scores' && renderScoreDistributionTable()}

      {activeTable === 'daily' && renderDailyTrendsTable()}

      {activeTable === 'managers' && renderManagerTable()}
    </div>
  );
};

export default EnhancedAnalyticsTables;
